import { useEffect, useState } from "react";
import { Copy, Check, Tag } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "./KitchenDiscountCarousel.css";
import { couponFetchApi } from "./services";

const ACCENT_COLORS = [
  { bg: "#eff6ff", text: "#1d4ed8", badge: "#dbeafe" },
  { bg: "#fef3c7", text: "#92400e", badge: "#fde68a" },
  { bg: "#dcfce7", text: "#166534", badge: "#bbf7d0" },
  { bg: "#fce7f3", text: "#9d174d", badge: "#fbcfe8" },
  { bg: "#f5f3ff", text: "#5b21b6", badge: "#ede9fe" },
];

const formatDiscount = (offer) => {
  if (!offer) return "Offer";
  if (offer.type === "fixed") return `৳${offer.value} OFF`;
  if (offer.type === "percentage") return `${offer.value}% OFF`;
  return `${offer.value} OFF`;
};

function CouponCard({ offer, index }) {
  const [copied, setCopied] = useState(false);
  const accent = ACCENT_COLORS[index % ACCENT_COLORS.length];

  const handleCopy = () => {
    navigator.clipboard.writeText(offer.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="kdc-card">
      {/* Discount badge */}
      <div className="kdc-badge" style={{ background: accent.bg, color: accent.text }}>
        <span className="kdc-badge-value">{formatDiscount(offer)}</span>
        {offer.minOrder > 0 && (
          <span className="kdc-badge-label">min ৳{offer.minOrder}</span>
        )}
      </div>

      {/* Code */}
      <div className="kdc-code-wrap">
        <span className="kdc-code-label">Coupon</span>
        <span className="kdc-code-value">{offer.code}</span>
      </div>

      {/* Copy icon */}
      <button
        className={`kdc-copy-btn${copied ? " copied" : ""}`}
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy code"}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>
    </div>
  );
}

const BREAKPOINTS = {
  1200: { slidesPerView: 4.4, spaceBetween: 12 },
  900:  { slidesPerView: 3.2, spaceBetween: 10 },
  600:  { slidesPerView: 2.2, spaceBetween: 10 },
};

export default function KitchenDiscountCarousel({ kitchenId }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!kitchenId) return;
    setLoading(true);
    couponFetchApi(kitchenId)
      .then((coupons) => setOffers(Array.isArray(coupons) ? coupons : []))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, [kitchenId]);

  if (!loading && !offers.length) return null;

  return (
    <div className="home-content-shell" style={{ paddingTop: 16, marginBottom: 24 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 14, paddingLeft: 2,
      }}>
        <Tag size={16} color="#0d6efd" />
        <h5 style={{
          fontWeight: 800, fontSize: 17, margin: 0,
          fontFamily: "'Inter', sans-serif", color: "#0f172a",
        }}>
          Coupons & Offers
        </h5>
      </div>

      {loading ? (
        <div style={{ display: "flex", gap: 10 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={62} width={180} borderRadius={12} />
          ))}
        </div>
      ) : (
        <Swiper
          breakpoints={BREAKPOINTS}
          spaceBetween={10}
          slidesPerView={1.8}
          loop={offers.length > 4}
        >
          {offers.map((offer, idx) => (
            <SwiperSlide key={offer._id || offer.code || idx}>
              <div style={{ padding: "4px 2px 8px" }}>
                <CouponCard offer={offer} index={idx} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}
